/**
 * The storefront catalog: three branches, each with its own product list.
 * Every sellable item has its own stable product id (branch + service id
 * pair), mirrored by the backend's payments.py, which holds the
 * authoritative charge amounts; the client only ever sends ids.
 * The site displays and charges USD; the GBP amounts stay here only as
 * the mirror of the backend catalog's legacy GBP column.
 */

export type BranchId = 'sales-and-trading' | 'investment-banking' | 'consulting';

export interface BranchFaq {
  q: string;
  a: string;
}

export interface Service {
  id: string;
  kind: 'single' | 'block' | 'combo' | 'package' | 'unlimited';
  name: string;
  /** Price in whole pounds. */
  price: number;
  /** Price in whole US dollars. */
  priceUsd: number;
  /** Sum of the parts, when buying separately costs more. */
  compareAt?: number;
  compareAtUsd?: number;
  blurb: string;
  features: string[];
  popular?: boolean;
}

export interface Branch {
  id: BranchId;
  name: string;
  shortName: string;
  heroTitle: string;
  heroLede: string;
  /** The services we help with, listed on the track card and branch hero. */
  drills: string[];
  services: Service[];
  seoTitle: string;
  seoDescription: string;
  faqs: BranchFaq[];
}

const RESUME: Service = {
  id: 'resume',
  kind: 'single',
  name: 'Resume Screening & Rework',
  price: 50,
  priceUsd: 65,
  blurb: 'Rebuilt line by line by someone who screens CVs for a living.',
  features: [
    'Line by line rework',
    '30 min call: your tutor reviews your resume with you'
  ]
};

const BEHAVIORAL: Service = {
  id: 'behavioral',
  kind: 'single',
  name: 'Behavioural Prep 1-on-1 (1 hour)',
  price: 129,
  priceUsd: 169,
  blurb: 'Your story, pressure-tested by someone who interviews candidates.',
  features: ['1 hour call', 'Behavioural prep']
};

const FULL_PACKAGE: Service = {
  id: 'full-package',
  kind: 'package',
  name: 'Offer-Ready Programme',
  price: 549,
  priceUsd: 729,
  compareAt: 645,
  compareAtUsd: 861,
  popular: true,
  blurb: 'Resume rework plus four calls of your choice.',
  features: ['Resume rework', 'Four 1 hour calls', 'Same tutor throughout']
};

const UNLIMITED: Service = {
  id: 'unlimited',
  kind: 'unlimited',
  name: 'Unlimited: One Recruiting Cycle',
  price: 1499,
  priceUsd: 1999,
  blurb: 'Every service, unlimited calls, for six months.',
  features: [
    'Unlimited calls for six months',
    'Every service included',
    'Message your tutor anytime'
  ]
};

export const BRANCHES: Branch[] = [
  {
    id: 'sales-and-trading',
    name: 'Sales & Trading',
    shortName: 'S&T',
    heroTitle: 'Sales & Trading interview prep',
    heroLede: 'What the floor actually asks, drilled 1-on-1 by tutors sitting on it now.',
    drills: [
      'Trade idea prep',
      'Market knowledge prep',
      'Brainteasers prep',
      'Resume advice',
      'HireVue questions',
      'Trading games'
    ],
    services: [
      RESUME,
      {
        id: 'technicals',
        kind: 'single',
        name: 'Desk Technicals (1 hour)',
        price: 149,
        priceUsd: 199,
        blurb: 'Markets knowledge and desk questions, on a call.',
        features: ['1 hour call', 'Technicals prep']
      },
      {
        id: 'trade-pitch',
        kind: 'single',
        name: 'Trade Pitch Preparation',
        price: 299,
        priceUsd: 399,
        blurb: 'Walk in with a pitch that survives.',
        features: ['Two 1 hour calls', 'Trade pitch prep']
      },
      BEHAVIORAL,
      {
        id: 'block-technicals-3',
        kind: 'block',
        name: 'Desk Technicals: 3-Session Block',
        price: 399,
        priceUsd: 529,
        compareAt: 447,
        compareAtUsd: 597,
        blurb: 'Three calls. Book as you need them.',
        features: ['Three 1 hour calls', 'Technicals prep', 'Use within 60 days']
      },
      {
        id: 'combo-resume-technicals',
        kind: 'combo',
        name: 'Resume + Technicals Combo',
        price: 179,
        priceUsd: 239,
        compareAt: 199,
        compareAtUsd: 264,
        blurb: 'The screen and the first round, one order.',
        features: ['Resume rework', 'One technicals call']
      },
      {
        id: 'combo-technicals-behavioral',
        kind: 'combo',
        name: 'Technicals + Behavioural Combo',
        price: 249,
        priceUsd: 329,
        compareAt: 278,
        compareAtUsd: 368,
        blurb: 'The whole interview: questions and fit.',
        features: ['One technicals call', 'One behavioural call']
      },
      FULL_PACKAGE,
      UNLIMITED
    ],
    seoTitle: 'Sales & Trading Interview Prep by Traders | Bulge Bracket',
    seoDescription:
      'Sales and trading interview prep, 1-on-1 with tutors on trading floors now: desk technicals, trade pitches, HireVue and CV rework. Pay, then book your tutor.',
    faqs: [
      {
        q: 'Which desks do you cover?',
        a: 'FX, rates, equities, credit, commodities and structuring, plus quant-leaning seats. Sessions are calibrated to your target desks.'
      },
      {
        q: 'Spring week or summer internship?',
        a: 'Both, plus off-cycles and grad seats. Sessions are pitched at the round you are facing.'
      },
      {
        q: 'I have no markets background. Too early?',
        a: 'No. Most first-years start there. The first session finds your level and sets the plan.'
      }
    ]
  },
  {
    id: 'investment-banking',
    name: 'Investment Banking',
    shortName: 'IB',
    heroTitle: 'Investment Banking interview prep',
    heroLede:
      'Accounting to paper LBO, plus the story banks score, drilled by bankers inside the firms on your list.',
    drills: [
      'Accounting & valuation prep',
      'Paper LBO drills',
      'Deal walkthroughs',
      'Resume advice',
      'HireVue questions',
      'Behavioural questions'
    ],
    services: [
      RESUME,
      {
        id: 'technicals',
        kind: 'single',
        name: 'IB Technicals (1 hour)',
        price: 149,
        priceUsd: 199,
        blurb: 'Accounting to LBO, on a call.',
        features: ['1 hour call', 'Technicals prep']
      },
      BEHAVIORAL,
      {
        id: 'block-technicals-3',
        kind: 'block',
        name: 'IB Technicals: 3-Session Block',
        price: 399,
        priceUsd: 529,
        compareAt: 447,
        compareAtUsd: 597,
        blurb: 'Three calls. Book as you need them.',
        features: ['Three 1 hour calls', 'Technicals prep', 'Use within 60 days']
      },
      {
        id: 'combo-resume-technicals',
        kind: 'combo',
        name: 'Resume + Technicals Combo',
        price: 179,
        priceUsd: 239,
        compareAt: 199,
        compareAtUsd: 264,
        blurb: 'The screen and the first round, one order.',
        features: ['Resume rework', 'One technicals call']
      },
      {
        id: 'combo-technicals-behavioral',
        kind: 'combo',
        name: 'Technicals + Behavioural Combo',
        price: 249,
        priceUsd: 329,
        compareAt: 278,
        compareAtUsd: 368,
        blurb: 'The whole interview: questions and fit.',
        features: ['One technicals call', 'One behavioural call']
      },
      FULL_PACKAGE,
      UNLIMITED
    ],
    seoTitle: 'Investment Banking Interview Prep by Bankers | Bulge Bracket',
    seoDescription:
      'Investment banking interview prep with bankers inside the industry: accounting to paper LBO, superday drills, CV rework. Pay online, book your tutor after.',
    faqs: [
      {
        q: 'Spring week, summer analyst or off-cycle?',
        a: 'All three. Deadlines, formats and the technical bar differ, so sessions are pitched at your stage.'
      },
      {
        q: 'I am at a non-target. Does this still work?',
        a: 'Non-targets are half our candidates. Technicals and the CV matter more when the school name does less.'
      },
      {
        q: 'How technical do interviews really get?',
        a: 'For interns: three statements, enterprise vs equity value, DCF mechanics, increasingly a paper LBO. Your tutor takes you up that ladder.'
      }
    ]
  },
  {
    id: 'consulting',
    name: 'Consulting',
    shortName: 'Consulting',
    heroTitle: 'Consulting case interview prep',
    heroLede:
      'Live cases with consultants at top strategy firms: structure, sizing, profitability and fit.',
    drills: [
      'Case interview prep',
      'Market sizing',
      'Profitability & frameworks',
      'Mental math drills',
      'Resume advice',
      'Fit interview prep'
    ],
    services: [
      RESUME,
      {
        id: 'case-study',
        kind: 'single',
        name: 'Live Case Session (1 hour)',
        price: 149,
        priceUsd: 199,
        blurb: 'A full case with a working consultant, on a call.',
        features: ['1 hour call', 'Live case prep']
      },
      BEHAVIORAL,
      {
        id: 'block-case-3',
        kind: 'block',
        name: 'Live Cases: 3-Session Block',
        price: 399,
        priceUsd: 529,
        compareAt: 447,
        compareAtUsd: 597,
        blurb: 'Three coached cases. Book as you need them.',
        features: ['Three 1 hour calls', 'Case prep', 'Use within 60 days']
      },
      {
        id: 'combo-resume-case',
        kind: 'combo',
        name: 'Resume + Case Combo',
        price: 179,
        priceUsd: 239,
        compareAt: 199,
        compareAtUsd: 264,
        blurb: 'The screen and the first case, one order.',
        features: ['Resume rework', 'One case call']
      },
      {
        id: 'combo-case-behavioral',
        kind: 'combo',
        name: 'Case + Behavioural Combo',
        price: 249,
        priceUsd: 329,
        compareAt: 278,
        compareAtUsd: 368,
        blurb: 'The whole interview: case and fit.',
        features: ['One case call', 'One behavioural call']
      },
      FULL_PACKAGE,
      UNLIMITED
    ],
    seoTitle: 'Consulting Case Interview Prep, 1-on-1 | Bulge Bracket',
    seoDescription:
      'Consulting case interview prep with consultants at top strategy firms: live cases, market sizing, fit rounds, CV rework. Pay online, book your tutor after.',
    faqs: [
      {
        q: 'Is this only for MBB?',
        a: 'No. We cover MBB, Big Four strategy arms and boutiques, and your tutor drills the case variant your interviews use.'
      },
      {
        q: 'How many cases before I am ready?',
        a: 'Most candidates need 15–25 live cases. Coached sessions fix habits; your tutor structures the peer practice between them.'
      },
      {
        q: 'Do you prep the fit interview too?',
        a: 'Yes. Behavioural sessions cover the personal experience interview that decides close calls.'
      }
    ]
  }
];

export const getBranch = (id: string | undefined): Branch | undefined =>
  BRANCHES.find(branch => branch.id === id);

export const formatUsd = (amount: number): string => `$${amount.toLocaleString('en-US')}`;

export const LOWEST_PRICE_USD = Math.min(
  ...BRANCHES.flatMap(branch => branch.services.map(service => service.priceUsd))
);
